import React, { useEffect, useState } from "react";
import { Button, Alert, Image } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../Context/AppContext";
import { useGetUser } from "../api/queries/useGetUser";
import ProfileModal from "./common/ProfileModal";
import { User } from "../types/Types";
import CurvedTextOverlay from "./Account/CurvedTextOverlay";
import { FaUniversity } from "react-icons/fa";
import { IoLocationOutline } from "react-icons/io5";
import { MdOutlineBusiness } from "react-icons/md";
import { useMediaQuery } from "react-responsive";

const cardData = [
  {
    title: "Profile",
    text: "Profile Preview.",
    link: "",
    logo: "/userIcon.png",
    isComingSoon: false,
  },
  {
    title: "Connections",
    text: "Manage your connections.",
    link: "/connections",
    logo: "/connect.png",
    isComingSoon: false,
  },
  {
    title: "Messages",
    text: "View and send messages.",
    link: "/messages",
    logo: "/message.png",
    isComingSoon: false,
  },
  {
    title: "Muslim Invest",
    text: "Our flagship shariah compliant investment match making service.\n\nFor businesses looking to raise funds and angel investors looking for opportunities.",
    link: "/investing",
    logo: "/MI.png",
    isComingSoon: false,
  },
  {
    title: "International Markets",
    text: "Explore business opportunities internationally.",
    link: "/international-markets",
    logo: "/MIM.png",
    isComingSoon: true,
  },
  {
    title: "Mentor Academy",
    text: "View Mentor Academy.",
    link: "/mentor-academy",
    logo: "/MMA.png",
    isComingSoon: true,
  },
  {
    title: "Venture Builder",
    text: "View Venture Builder.",
    link: "/venture-builder",
    logo: "/MVB.png",
    isComingSoon: true,
  },
];

const Dashboard: React.FC = () => {
  const [showProfileModal, setShowProfileModal] = useState(false);
  const navigate = useNavigate();
  const { setUser, objectId } = useAppContext();
  const {
    data: userData,
    isPending,
    isFetching,
  } = useGetUser({
    id: objectId || "",
    enabled: !!objectId,
  });

  useEffect(() => {
    if (userData) {
      setUser(userData);
    }
  }, [userData, setUser]);

  const handleCompleteProfile = () => {
    if (userData?.user?.membershipName === "Youth Membership") {
      navigate("/youth-membership-form");
    } else {
      navigate("/business-membership-form");
    }
  };

  const selectedUser = { ...userData?.user, ...userData?.imageUrls };

  const percentData = { ...userData?.user, ...userData?.imageUrls };
  const fields = Object.entries(percentData || {}).filter(
    ([key, value]) =>
      key.trim() !== "" &&
      value !== null &&
      value !== "" &&
      ![
        "password",
        "objectId",
        "isGDPRConsent",
        "isEmailUpdates",
        "isTermConditions",
        "isDirectory",
        "hasPaid",
        "hasCompletedProfile",
        "id",
        "membershipId",
        "consentToShareProfile",
        "isAdmin",
        "membershipType",
        "discountEndDate",
        "discountCode",
        "membershipName",
        "isAnnualMembership",
        "BusinessLogo",
        "PersonalProfessionalPhoto",
        "businessAddressLineTwo",
        "county",
        "businessAbout",
        "personalAbout",
        "businessProductServices",
        "businessDiscountCode",
        "businessDiscount",
        "ProfessionalPhoto",
        "skillsExperience",
      ].includes(key)
  );
  const totalPossibleFields = 18;
  const filledFieldsCount = fields.length;
  const completionPercent = Math.min(
    Math.round((filledFieldsCount / totalPossibleFields) * 100),
    100
  );
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLgTablet = useMediaQuery({ minWidth: 992, maxWidth: 1023 });
  const isMdTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });
  const imageSize = isDesktop ? 150 : isLgTablet ? 140 : isMdTablet ? 130 : 95;

  if (!objectId || isPending || isFetching || !userData?.user) {
    return (
      <div className="container p-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-100 p-4 pt-5 pt-md-5 m-0">
      {completionPercent < 100 && (
        <Alert
          variant="danger"
          className="mb-4 rounded-4 shadow-lg d-flex flex-column flex-md-row justify-content-between align-items-center gap-2"
        >
          <div className="text-center text-md-left">
            Profile Incomplete: Please complete your profile to continue.
          </div>
          <Button className="px-4 btn" onClick={handleCompleteProfile}>
            Complete Profile
          </Button>
        </Alert>
      )}

      <div className="card shadow-lg border-0 pb-2">
        <div>
          <div
            className={`${
              selectedUser.BannerImage ? "" : "gradient-bg"
            } position-relative profile-banner rounded-top d-flex justify-content-center align-items-center`}
            style={{
              backgroundImage: `url(${selectedUser?.BannerImage ?? ""})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              width: "100%",
              height: "100%",
              aspectRatio: "3 / 1",
            }}
          >
            {!selectedUser.BannerImage && (
              <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                <img
                  src="/SMBF-logo-white.png"
                  alt="logo"
                  className="w-25 h-25"
                />
              </div>
            )}{" "}
            <div className="position-absolute translate-middle image-container">
              <div className="position-relative w-100 h-100">
                <Image
                  src={
                    selectedUser.ProfessionalPhoto ||
                    selectedUser.PersonalProfessionalPhoto ||
                    "/user.png"
                  }
                  roundedCircle
                  alt="Profile"
                  className="profile-image w-100 h-100 shadow-sm border border-2 border-white p-1"
                />

                <span className="badge-overlay text-white fw-bold px-2 py-1 rounded-pill">
                  <CurvedTextOverlay
                    size={imageSize}
                    stroke={
                      selectedUser?.membershipName === "Youth Membership"
                        ? "#18c2c6"
                        : selectedUser?.membershipName === "Business Membership"
                        ? "#1c57b9"
                        : "#544bc1"
                    }
                    text={
                      selectedUser?.membershipName === "Youth Membership"
                        ? "#Youth"
                        : selectedUser?.membershipName === "Business Membership"
                        ? "#Business"
                        : "#Executive"
                    }
                  />
                </span>
              </div>
            </div>
          </div>
          <div className="mx-3 pt-5 flex-grow-1">
            <div className="d-flex justify-content-between align-items-center">
              <div className="mt-md-4 mt-lg-5">
                <h2 className="mb-0">
                  {(selectedUser.firstName || "").charAt(0).toUpperCase() +
                    (selectedUser.firstName || "").slice(1) +
                    " " +
                    ((selectedUser.lastName || "").charAt(0).toUpperCase() +
                      (selectedUser.lastName || "").slice(1))}
                </h2>
                <span>
                  {selectedUser.membershipName === "Youth Membership"
                    ? selectedUser.studyInterest &&
                      selectedUser.studyInterest.charAt(0).toUpperCase() +
                        selectedUser.studyInterest.slice(1)
                    : selectedUser.personalPositionCompany &&
                      selectedUser.personalPositionCompany
                        .charAt(0)
                        .toUpperCase() +
                        selectedUser.personalPositionCompany.slice(1)}
                </span>
                {selectedUser.businessSectorIndustry ? (
                  <div className="d-flex gap-1">
                    <span className="text-muted">
                      {selectedUser.businessSectorIndustry}
                    </span>
                  </div>
                ) : (
                  ""
                )}
              </div>
              <div className="d-none d-md-block">
                {selectedUser.membershipName === "Youth Membership" ? (
                  <div className="fw-semibold d-flex gap-1 align-items-center">
                    <FaUniversity size={25} />
                    {selectedUser.universityCollege}
                  </div>
                ) : (
                  <div className="d-flex gap-3 align-items-center cursor-pointer">
                    {selectedUser.businessLogo ? (
                      <img
                        src={selectedUser.BusinessLogo}
                        className="rounded-circle"
                        width={40}
                        height={40}
                        alt={"logo"}
                      />
                    ) : (
                      <MdOutlineBusiness size={30} />
                    )}
                    <div className="fw-semibold">
                      {selectedUser.businessName}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-4">
        {cardData.map((card, index) => (
          <div
            style={{ minHeight: "20vh" }}
            className="col-md-4 mb-4"
            key={index}
          >
            <div className="card h-100 shadow-lg rounded-4 border-0 position-relative">
              <div className="card-body d-flex flex-column justify-content-between p-3">
                <div className="d-flex flex-row justify-content-between w-100">
                  <div>
                    <h5>{card.title}</h5>
                    <p className="card-text text-muted" style={{ whiteSpace: "pre-line" }}>{card.text}</p>
                  </div>
                  {card.logo && (
                    <img src={card.logo} alt={card.title} height="30" />
                  )}
                </div>
                <Button
                  onClick={() => {
                    if (card.title === "Profile") {
                      setShowProfileModal(true);
                    } else {
                      navigate(card.link);
                    }
                  }}
                  className="btn w-100 mt-2"
                  disabled={
                    card.isComingSoon ||
                    (card.title === "Muslim Invest" &&
                      userData?.user?.membershipName === "Youth Membership")
                  }
                >
                  {card.isComingSoon
                    ? "Coming Soon"
                    : card.title === "Profile"
                    ? "Profile Preview"
                    : card.title === "Muslim Invest" &&
                      userData?.user?.membershipName === "Youth Membership"
                    ? "Upgrade to Business Membership"
                    : `Go to ${card.title}`}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {showProfileModal && (
        <ProfileModal
          isConnected
          show={showProfileModal}
          onHide={() => setShowProfileModal(false)}
          selectedUser={{ ...userData?.user, ...userData?.imageUrls } as User}
        />
      )}
    </div>
  );
};

export default Dashboard;